import { useState } from 'react';
import * as XLSX from 'xlsx';
import { X, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FIELD_CATEGORIES, getEnabledFields } from '@/lib/employeeFields';

// Bulk/export both deal in flat rows — object/repeatable (nominee, family
// members, witnesses) and photo don't translate to a spreadsheet column,
// so they're excluded here the same way they are from the bulk template.
const EXPORT_EXCLUDED_TYPES = ['object', 'repeatable', 'photo'];

function getValue(emp, field) {
  if (field.key === 'department_id') return emp.department?.name || emp.department || '';
  const raw = field.target === 'column' ? emp[field.key] : emp.extended_details?.[field.key];
  if (field.type === 'boolean') return raw === false ? 'No' : raw === true ? 'Yes' : '';
  return raw ?? '';
}

export function ExportModal({ onClose, employees, enabledList, filterSummary }) {
  const exportableFields = getEnabledFields(enabledList).filter(f => f.key !== 'employee_code' && !EXPORT_EXCLUDED_TYPES.includes(f.type));
  const [selected, setSelected] = useState(() => new Set(exportableFields.map(f => f.key)));
  const [groupByDept, setGroupByDept] = useState(false);

  const toggle = (key) => setSelected(s => {
    const next = new Set(s);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const columns = exportableFields.filter(f => selected.has(f.key));

  const buildRows = (rows) => rows.map(e => {
    const row = { 'Employee Code': e.employee_code };
    columns.forEach(f => { row[f.label] = getValue(e, f); });
    return row;
  });

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    if (groupByDept) {
      const groups = {};
      employees.forEach(e => {
        const dept = e.department?.name || e.department || 'Unassigned';
        (groups[dept] = groups[dept] || []).push(e);
      });
      Object.entries(groups).forEach(([dept, rows]) => {
        const ws = XLSX.utils.json_to_sheet(buildRows(rows));
        XLSX.utils.book_append_sheet(wb, ws, dept.slice(0, 31));
      });
    } else {
      const ws = XLSX.utils.json_to_sheet(buildRows(employees));
      XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    }
    XLSX.writeFile(wb, `attendx-employees-export-${new Date().toISOString().slice(0, 10)}.xlsx`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-[16px] max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-card)]">
          <h2 className="text-[16px] font-bold text-[var(--text-primary)]">Export Employees</h2>
          <button onClick={onClose} className="p-1.5 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-[10px] bg-[var(--bg-muted)] p-3 text-[12px] text-[var(--text-secondary)]">
            <p className="font-semibold text-[var(--text-tertiary)] uppercase tracking-wide text-[11px] mb-1">Active Filters</p>
            {filterSummary || 'None — exporting all employees'}
            <p className="mt-1"><strong>{employees.length}</strong> employee{employees.length !== 1 ? 's' : ''} will be exported</p>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-[13px] font-medium text-[var(--text-primary)]">Group by department</span>
            <button onClick={() => setGroupByDept(g => !g)}
              className={`w-9 h-5 rounded-full transition-colors relative ${groupByDept ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${groupByDept ? 'left-4' : 'left-0.5'}`}/>
            </button>
          </div>
          <p className="text-[11px] text-[var(--text-tertiary)] -mt-2">{groupByDept ? 'One sheet per department in the workbook.' : 'All employees on a single sheet.'}</p>

          <div>
            <p className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-2">Columns</p>
            <div className="space-y-3 max-h-[260px] overflow-y-auto">
              {FIELD_CATEGORIES.map(cat => {
                const fields = exportableFields.filter(f => f.category === cat.key);
                if (!fields.length) return null;
                return (
                  <div key={cat.key}>
                    <p className="text-[11px] font-medium text-[var(--text-tertiary)] mb-1">{cat.label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {fields.map(f => (
                        <label key={f.key} className="flex items-center gap-1.5 px-2 py-1 rounded-[6px] bg-[var(--bg-muted)] text-[12px] cursor-pointer">
                          <input type="checkbox" checked={selected.has(f.key)} onChange={() => toggle(f.key)} className="accent-[var(--accent)]"/>
                          {f.label}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Button icon={FileSpreadsheet} onClick={handleExport} disabled={!employees.length} className="w-full">
            Export {employees.length} Employee{employees.length !== 1 ? 's' : ''} to Excel
          </Button>
        </div>
      </div>
    </div>
  );
}
